import React, { useEffect, useRef } from 'react';
import {
  X,
  Activity,
  Play,
  CheckCircle,
  XCircle,
  ArrowRight,
  Clock,
  Zap,
  Settings
} from 'lucide-react';

const V2EventLog = ({ events, runId, onClose }) => {
  const eventsEndRef = useRef(null);

  // Auto scroll to bottom when new events arrive
  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'workflow.started':
      case 'workflow.execution_started':
        return <Play className="w-4 h-4" />;
      case 'step.started':
        return <Activity className="w-4 h-4" />;
      case 'step.completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'step.failed':
        return <XCircle className="w-4 h-4" />;
      case 'step.transition':
        return <ArrowRight className="w-4 h-4" />;
      case 'connection.established':
        return <Zap className="w-4 h-4" />;
      default:
        return <Settings className="w-4 h-4" />;
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'workflow.started':
      case 'workflow.execution_started':
        return 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20';
      case 'step.started':
        return 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/20';
      case 'step.completed':
        return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20';
      case 'step.failed':
        return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20';
      case 'step.transition':
        return 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20';
      case 'connection.established':
        return 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20';
      default:
        return 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const formatEventTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventTitle = (event) => {
    const eventType = event.event_type;
    const data = event.data || {};

    switch (eventType) {
      case 'workflow.started':
        return 'Workflow Started';
      case 'workflow.execution_started':
        return `Executing: ${data.workflow_config?.name || 'Workflow'}`;
      case 'step.started':
        return `Starting: ${data.step_name || 'Unknown Step'}`;
      case 'step.completed':
        return `Completed: ${data.step_name || 'Unknown Step'}`;
      case 'step.failed':
        return `Failed: ${data.step_name || 'Unknown Step'}`;
      case 'step.transition':
        return `${data.from_step} → ${data.to_step}`;
      case 'connection.established':
        return 'Connection Established';
      default:
        return eventType.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getEventDetails = (event) => {
    const eventType = event.event_type;
    const data = event.data || {};

    const details = [];

    // Common details
    if (data.tokens_used) {
      details.push(`${data.tokens_used} tokens`);
    }
    if (data.execution_time_ms) {
      details.push(`${data.execution_time_ms}ms`);
    }
    if (data.step_type) {
      details.push(`Type: ${data.step_type}`);
    }

    // Event-specific details
    switch (eventType) {
      case 'step.failed':
        if (data.error_message) {
          details.push(`Error: ${data.error_message}`);
        }
        break;
      case 'step.transition':
        if (data.transition_type) {
          details.push(`Mode: ${data.transition_type}`);
        }
        break;
    }

    return details;
  };

  const EmptyState = () => (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="text-center">
        <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
          No Events Yet
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Workflow events will appear here as they occur
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h3 className="font-medium text-gray-900 dark:text-white">Live Events</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Run ID */}
      {runId && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Run ID: <span className="font-mono">{runId.slice(0, 16)}...</span>
          </div>
        </div>
      )}

      {/* Events list */}
      <div className="flex-1 overflow-y-auto">
        {events.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="p-2 space-y-2">
            {events.map((event, index) => {
              const colorClasses = getEventColor(event.event_type);
              const details = getEventDetails(event);

              return (
                <div
                  key={`${event.event_id || index}`}
                  className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm"
                >
                  {/* Event header */}
                  <div className="flex items-start space-x-3">
                    <div className={`p-1.5 rounded-full ${colorClasses}`}>
                      {getEventIcon(event.event_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {getEventTitle(event)}
                        </h4>
                        <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="w-3 h-3" />
                          <span>{formatEventTime(event.timestamp)}</span>
                        </div>
                      </div>

                      {/* Event details */}
                      {details.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {details.map((detail, detailIndex) => (
                            <span
                              key={detailIndex}
                              className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 rounded"
                            >
                              {detail}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Raw event type for debugging */}
                      <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 font-mono">
                        {event.event_type}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={eventsEndRef} />
          </div>
        )}
      </div>

      {/* Footer stats */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {events.length} event{events.length !== 1 ? 's' : ''} received
        </div>
      </div>
    </div>
  );
};

export default V2EventLog;